import { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import LayerHeader from '../components/ui/LayerHeader';
import { NumCell } from '../components/ui/NumCell';
import { TH, makeSticky } from '../components/ui/gridTable';
import Dropdown from '../components/receptura/Dropdown';
import useSirokyDisplej from '../hooks/useSirokyDisplej';
import { fmt, num, fmtKc, MESICE_SHORT, MKEYS } from '../lib/formatters';
import { ACCENT, OK, WARN, BAD, W_MESIC, tableSurfaces } from '../lib/uiTokens';
import { AopTable, BtnUlozit, LayerSpinner } from '../components/ui/aopUi';

// Povaha nákladu — drží číselník, tady jen štítek.
const FVS = {
    fix:  { zkratka: 'F', nazev: 'Fixní',          barva: BAD },
    var:  { zkratka: 'V', nazev: 'Variabilní',     barva: OK },
    semi: { zkratka: 'S', nazev: 'Semi-variabilní', barva: WARN },
};

function FvsBadge({ typ }) {
    const d = FVS[typ] || FVS.fix;
    return (
        <span title={d.nazev} className="inline-flex items-center justify-center w-5 h-5 rounded text-[10px] font-bold"
            style={{ background: `${d.barva}22`, color: d.barva }}>
            {d.zkratka}
        </span>
    );
}

export default function FinanceAopOstatni({ rok, variantaId, cardStyle, isDark }) {
    const siroky = useSirokyDisplej();
    const [ucty, setUcty] = useState([]);       // číselník: účet → subúčty (pevný, needitovatelný)
    const [rows, setRows] = useState({});       // { [kategorie_id]: řádek }
    const [dirty, setDirty] = useState(() => new Set());
    const [otevrene, setOtevrene] = useState(() => new Set());
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    const headers = { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token };

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const q = new URLSearchParams({ rok });
            if (variantaId) q.set('varianta_id', variantaId);
            const d = await fetch(`/api/finance/aop/ostatni?${q}`).then(r => r.json());
            setUcty(Array.isArray(d.ucty) ? d.ucty : []);
            setRows(Object.fromEntries((d.radky || []).map(r => [r.kategorie_id, r])));
            setDirty(new Set());
        } catch (e) { console.error(e); }
        setLoading(false);
    }, [rok, variantaId]);

    useEffect(() => { loadData(); }, [loadData]);

    const updateCell = (katId, field, value) => {
        setRows(prev => ({ ...prev, [katId]: { ...prev[katId], [field]: value } }));
        setDirty(prev => new Set(prev).add(katId));
    };
    // „Kč/měsíc" → rozkopíruje částku do všech 12 měsíců
    const setVsechnyMesice = (katId, value) => {
        setRows(prev => ({ ...prev, [katId]: { ...prev[katId], ...Object.fromEntries(MKEYS.map(k => [k, value])) } }));
        setDirty(prev => new Set(prev).add(katId));
    };

    const toggle = (id) => setOtevrene(prev => {
        const n = new Set(prev);
        n.has(id) ? n.delete(id) : n.add(id);
        return n;
    });
    const rozbalitVse = () => setOtevrene(new Set(ucty.map(u => u.id)));
    const sbalitVse = () => setOtevrene(new Set());

    const handleUlozit = async () => {
        if (dirty.size === 0) return;
        setSaving(true);
        try {
            await Promise.all([...dirty].map(katId => {
                const r = rows[katId];
                if (!r) return null;
                const body = {
                    dph_sazba: Number(r.dph_sazba) || 0,
                    poznamka: r.poznamka || null,
                    ...Object.fromEntries(MKEYS.map(k => [k, num(r[k])])),
                };
                return fetch(`/api/finance/aop/ostatni/${r.id}`, { method: 'PUT', headers, body: JSON.stringify(body) });
            }));
            setDirty(new Set());
        } catch (e) { alert('Chyba při ukládání'); }
        setSaving(false);
    };

    // ── Realtime součty — vše se počítá ze `rows`, žádný fetch ──
    const soucty = useMemo(() => {
        const radek = (katId) => rows[katId];
        const subMesice = (katId) => MKEYS.map(k => num(radek(katId)?.[k]));
        const ucetMesice = (u) => u.subucty.reduce(
            (acc, s) => { const m = subMesice(s.id); return acc.map((v, i) => v + m[i]); },
            Array(12).fill(0),
        );

        const perUcet = {};
        ucty.forEach(u => { perUcet[u.id] = ucetMesice(u); });

        const celkem = ucty.reduce(
            (acc, u) => acc.map((v, i) => v + perUcet[u.id][i]),
            Array(12).fill(0),
        );

        // Rozpad dle povahy nákladu (F/V/S) — roční
        const dleTypu = { fix: 0, var: 0, semi: 0 };
        ucty.forEach(u => u.subucty.forEach(s => {
            const rocni = subMesice(s.id).reduce((a, b) => a + b, 0);
            if (dleTypu[s.typ_fvs] != null) dleTypu[s.typ_fvs] += rocni;
        }));

        return { perUcet, celkem, dleTypu, subMesice };
    }, [rows, ucty]);

    if (loading) {
        return <LayerSpinner />;
    }

    const rocniZ = (m) => m.reduce((a, b) => a + b, 0);
    const rokCelkem = rocniZ(soucty.celkem);
    // Stejná částka ve všech 12 měsících → zobrazí se v „Kč/měsíc"
    const stejne = (katId) => {
        const m = soucty.subMesice(katId);
        return m.every(v => v === m[0]) ? (m[0] || '') : '';
    };

    const { headBg, groupBg: ucetBg, stickyBg } = tableSurfaces(isDark);
    const th = TH;
    const colW = { nazev: 260, fvs: 44, dph: 64, mesicne: 116, rocni: 120, mesic: W_MESIC };
    const NCOLS = 5 + 12;

    // Zamrzlý levý blok — na užším displeji jen název účtu (celý blok má 604 px)
    const LEFT = {
        nazev: 0,
        fvs: colW.nazev,
        dph: colW.nazev + colW.fvs,
        mesicne: colW.nazev + colW.fvs + colW.dph,
        rocni: colW.nazev + colW.fvs + colW.dph + colW.mesicne,
    };
    const sticky = makeSticky(LEFT, 'rocni', siroky ? null : ['nazev']);

    const btn = {
        background: isDark ? 'rgba(255,255,255,0.06)' : 'var(--surface-raised)',
        border: '1px solid var(--border-default)', color: 'var(--text-primary)',
    };

    return (
        <div className="flex flex-col flex-1 min-h-0">
            {/* Hlavička vrstvy */}
            <LayerHeader krok={5} title="Ostatní náklady" cardStyle={cardStyle}>
                    Pevná osnova <b>účet → subúčet</b> (A–H). Každý subúčet má povahu
                    <b style={{ color: FVS.fix.barva }}> F</b> fixní ·
                    <b style={{ color: FVS.var.barva }}> V</b> variabilní ·
                    <b style={{ color: FVS.semi.barva }}> S</b> semi-variabilní (fixní základ + složka rostoucí s objemem).
                    Zadej buď jednu částku do „Kč/měsíc" (rozkopíruje se do všech 12 měsíců), nebo rozepiš jednotlivé měsíce.
                    Účty rozbal kliknutím; součty se přepočítávají průběžně. Do Výsledovky jde plán per subúčet.
            </LayerHeader>

            {/* Toolbar */}
            <div className="flex items-center gap-2 mb-4 flex-wrap flex-shrink-0">
                <button onClick={rozbalitVse} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium" style={btn}>
                    <ChevronDown size={14} /> Rozbalit vše
                </button>
                <button onClick={sbalitVse} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium" style={btn}>
                    <ChevronRight size={14} /> Sbalit vše
                </button>
                <div className="flex-1" />
                <div className="flex items-center gap-3 text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
                    {Object.entries(FVS).map(([k, d]) => (
                        <span key={k}>{d.zkratka}: <b style={{ color: d.barva }}>{fmt(soucty.dleTypu[k])}</b></span>
                    ))}
                    <span style={{ color: 'var(--text-tertiary)' }}>Celkem: <b style={{ color: 'var(--text-primary)' }}>{fmtKc(rokCelkem)}</b> / rok</span>
                </div>
                <BtnUlozit onClick={handleUlozit} saving={saving} dirtyCount={dirty.size} />
            </div>

            {/* Tabulka */}
            <AopTable cardStyle={cardStyle}>
                        <colgroup>
                            <col style={{ width: colW.nazev }} />
                            <col style={{ width: colW.fvs }} />
                            <col style={{ width: colW.dph }} />
                            <col style={{ width: colW.mesicne }} />
                            <col style={{ width: colW.rocni }} />
                            {MKEYS.map(k => <col key={k} style={{ width: colW.mesic }} />)}
                        </colgroup>
                        <thead>
                            <tr style={{ borderBottom: '1px solid var(--border-default)' }}>
                                <th className={`${th} text-left`} style={{ ...sticky('nazev', 30, headBg), color: 'var(--text-secondary)' }}>Účet / subúčet</th>
                                <th className={`${th} text-center`} style={{ ...sticky('fvs', 30, headBg), color: 'var(--text-tertiary)' }}>F/V/S</th>
                                <th className={`${th} text-center`} style={{ ...sticky('dph', 30, headBg), color: 'var(--text-tertiary)' }}>DPH</th>
                                <th className={`${th} text-right`} style={{ ...sticky('mesicne', 30, headBg), color: 'var(--text-tertiary)' }}>Kč/měsíc</th>
                                <th className={`${th} text-right`} style={{ ...sticky('rocni', 30, headBg), color: ACCENT }}>Roční</th>
                                {MESICE_SHORT.map((m, i) => <th key={i} className={`${th} text-right`} style={{ background: headBg, color: 'var(--text-tertiary)' }}>{m}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {ucty.map(u => {
                                const um = soucty.perUcet[u.id] || Array(12).fill(0);
                                const otevreno = otevrene.has(u.id);
                                const Chevron = otevreno ? ChevronDown : ChevronRight;
                                return (
                                    <Fragment key={u.id}>
                                        {/* Účet — sbalený součet, klik rozbalí */}
                                        <tr onClick={() => toggle(u.id)} className="cursor-pointer"
                                            style={{ background: ucetBg, borderTop: '2px solid var(--border-default)' }}>
                                            <td className="px-2 py-2" style={sticky('nazev', 10, ucetBg)}>
                                                <div className="flex items-center gap-1.5">
                                                    <Chevron size={14} style={{ color: 'var(--text-tertiary)' }} />
                                                    <span className="text-[13px] font-bold uppercase tracking-wide" style={{ color: 'var(--text-primary)' }}>{u.nazev}</span>
                                                    <span className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>({u.subucty.length})</span>
                                                </div>
                                            </td>
                                            <td style={sticky('fvs', 10, ucetBg)} />
                                            <td style={sticky('dph', 10, ucetBg)} />
                                            <td style={sticky('mesicne', 10, ucetBg)} />
                                            <td className="px-2 py-2 text-right text-[13px] font-mono font-bold tabular-nums" style={{ ...sticky('rocni', 10, ucetBg), color: 'var(--text-primary)' }}>{fmt(rocniZ(um))}</td>
                                            {um.map((v, i) => (
                                                <td key={i} className="px-2 py-2 text-right text-[11px] font-mono tabular-nums" style={{ color: 'var(--text-tertiary)' }}>{v ? fmt(v) : ''}</td>
                                            ))}
                                        </tr>

                                        {/* Subúčty */}
                                        {otevreno && u.subucty.map(s => {
                                            const r = rows[s.id];
                                            if (!r) return null;
                                            const sm = soucty.subMesice(s.id);
                                            return (
                                                <tr key={s.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                                                    <td className="px-2 py-1 text-[14px]" style={{ ...sticky('nazev', 10, stickyBg), paddingLeft: 30, color: 'var(--text-primary)' }}>{s.nazev}</td>
                                                    <td className="px-1 py-1 text-center" style={sticky('fvs', 10, stickyBg)}><FvsBadge typ={s.typ_fvs} /></td>
                                                    <td className="px-1 py-1 text-center" style={sticky('dph', 10, stickyBg)}>
                                                        <Dropdown value={r.dph_sazba ?? 21} onChange={v => updateCell(s.id, 'dph_sazba', Number(v))}
                                                            options={[{ value: 0, label: '0 %' }, { value: 12, label: '12 %' }, { value: 21, label: '21 %' }]}
                                                            isDark={isDark} accent="#3B82F6" height={28} />
                                                    </td>
                                                    <td style={sticky('mesicne', 10, stickyBg)}><NumCell value={stejne(s.id)} onChange={v => setVsechnyMesice(s.id, v)} color="var(--text-secondary)" placeholder="—" sm blankEmpty /></td>
                                                    <td className="px-2 py-1 text-right text-[14px] font-mono font-semibold tabular-nums" style={{ ...sticky('rocni', 10, stickyBg), color: 'var(--text-primary)' }}>{fmtKc(rocniZ(sm))}</td>
                                                    {MKEYS.map(k => <td key={k}><NumCell value={r[k]} onChange={v => updateCell(s.id, k, v)} color="var(--text-primary)" sm blankEmpty /></td>)}
                                                </tr>
                                            );
                                        })}
                                    </Fragment>
                                );
                            })}

                            {ucty.length === 0 && (
                                <tr><td colSpan={NCOLS} className="px-4 py-8 text-center text-sm" style={{ color: 'var(--text-tertiary)' }}>
                                    Číselník ostatních nákladů je prázdný — chybí seed migrace.
                                </td></tr>
                            )}
                        </tbody>
                        <tfoot>
                            <tr style={{ borderTop: '2px solid var(--border-default)', background: headBg }}>
                                {/* Bez colSpan — jinak by zamrzla šířka všech čtyř sloupců místo názvu */}
                                <td className="px-2 py-2.5 text-[15px] font-bold whitespace-nowrap" style={{ ...sticky('nazev', 20, headBg), color: 'var(--text-primary)' }}>Ostatní náklady celkem</td>
                                <td style={sticky('fvs', 20, headBg)} />
                                <td style={sticky('dph', 20, headBg)} />
                                <td style={sticky('mesicne', 20, headBg)} />
                                <td className="px-2 py-2.5 text-right text-[14px] font-mono font-bold tabular-nums" style={{ ...sticky('rocni', 20, headBg), color: 'var(--text-primary)' }}>{fmtKc(rokCelkem)}</td>
                                {soucty.celkem.map((s, i) => <td key={i} className="px-2 py-2.5 text-right text-[13px] font-mono tabular-nums" style={{ color: 'var(--text-secondary)' }}>{fmtKc(s)}</td>)}
                            </tr>
                        </tfoot>
                    </AopTable>
        </div>
    );
}
